"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Avatar } from "@/components/ui/Avatar";
import { apiFetch } from "@/lib/api-client";

export function UserMenu({
  displayName,
  email,
  unreadCount,
}: {
  displayName: string;
  email: string;
  unreadCount: number;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function onLogout() {
    setOpen(false);
    await apiFetch("/api/auth/logout", { method: "POST" });
    router.push("/");
    router.refresh();
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-surface-soft"
      >
        <div className="relative">
          <Avatar name={displayName} seed={email} />
          {unreadCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold leading-none text-white"
              aria-label={`읽지 않은 알림 ${unreadCount}건`}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>
        <span className="hidden text-sm font-medium text-ink sm:inline">{displayName}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-48 overflow-hidden rounded-md border border-hairline bg-canvas py-1 shadow-lg"
        >
          <MenuLink href="/account" onNavigate={() => setOpen(false)}>
            마이페이지
          </MenuLink>
          <MenuLink href="/notifications" onNavigate={() => setOpen(false)}>
            알림
          </MenuLink>
          <MenuLink href="/help" onNavigate={() => setOpen(false)}>
            도움말
          </MenuLink>
          <div className="my-1 h-px bg-hairline-soft" />
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className="block w-full px-4 py-2.5 text-left text-sm text-error hover:bg-surface-soft"
          >
            로그아웃
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({ href, children, onNavigate }: { href: string; children: ReactNode; onNavigate: () => void }) {
  return (
    <Link
      href={href}
      role="menuitem"
      onClick={onNavigate}
      className="block px-4 py-2.5 text-sm text-ink hover:bg-surface-soft"
    >
      {children}
    </Link>
  );
}
